import { Location } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { GurdServiceService } from 'src/app/Service/gurd-service.service';
import { NotificationService } from 'src/app/Service/notification.service';
import { OtherService } from 'src/app/Service/other.service';
import { ReportExportService } from '../../report-export.service';
import { ReportContext, ReportDefinition } from '../../report.models';
import { toEnglishDigits } from '../../report.utils';
import { GeofenceReportApiService } from './geofence-report-api.service';
import { GeofenceApiStatus, GeofenceArea, GeofenceColumn, GeofenceReportConfig, GeofenceReportRow, ReportCredentials } from './geofence-report.models';
import { buildGeofenceSearchIndex, formatMinutes, formatSeconds, GEOFENCE_PAGE_SIZE, GEOFENCE_PAGE_SIZE_OPTIONS, getGeofenceConfig, sumGeofenceField } from './geofence-report.utils';

@Component({
  selector: 'app-geofence-report',
  templateUrl: './geofence-report.component.html',
  styleUrls: ['./geofence-report.component.css'],
})
export class GeofenceReportComponent implements OnChanges {
  @Input() context!: ReportContext;
  @Input() definition!: ReportDefinition;

  config!: GeofenceReportConfig;
  areas: GeofenceArea[] = [];
  selectedArea = '';
  rows: GeofenceReportRow[] = [];
  filteredRows: GeofenceReportRow[] = [];
  pagedRows: GeofenceReportRow[] = [];
  searchText = '';
  filtersOpen = false;
  readonly inspectionFilterForm = new FormGroup({
    startDate: new FormControl(''),
    endDate: new FormControl(''),
  });
  currentPage = 1;
  pageSize = GEOFENCE_PAGE_SIZE;
  readonly pageSizeOptions = GEOFENCE_PAGE_SIZE_OPTIONS;
  totalPages = 1;
  loading = false;
  hasError = false;
  hasRequestedData = false;
  totals: Record<string, number> = {};
  private requestVersion = 0;

  constructor(
    private readonly api: GeofenceReportApiService,
    private readonly exportService: ReportExportService,
    private readonly gurdService: GurdServiceService,
    private readonly notificationService: NotificationService,
    private readonly otherService: OtherService,
    private readonly router: Router,
    private readonly location: Location,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ((!changes['context'] && !changes['definition']) || !this.context || !this.definition?.geofenceMode) return;
    this.config = getGeofenceConfig(this.definition.geofenceMode);
    this.requestVersion++;
    this.setLoading(false);
    this.searchText = '';
    this.selectedArea = '';
    this.filtersOpen = this.requiresFilterBeforeLoad;
    this.inspectionFilterForm.setValue({
      startDate: this.context.startDate,
      endDate: this.context.endDate,
    });
    this.rows = [];
    this.refreshTable(true);
    this.initializeReport();
  }

  async initializeReport(): Promise<void> {
    if (this.isAreaSelectionReport) {
      await this.loadAreas();
      return;
    }
    if (this.config.mode === 'inspection-detail') return;
    await this.loadReport();
  }

  async loadAreas(): Promise<void> {
    if (this.loading) return;
    const requestVersion = ++this.requestVersion;
    this.setLoading(true);
    this.hasError = false;
    try {
      const result = await this.api.loadAreas(this.credentials);
      if (requestVersion !== this.requestVersion) return;
      if (this.handleStatus(result)) return;
      this.areas = Array.isArray(result) ? result : [];
      const storedArea = localStorage.getItem('ddval');
      if (storedArea) {
        const decryptedArea = this.gurdService.decrypt(storedArea) || storedArea;
        if (this.areas.some((area) => String(area.iGeoFence) === String(decryptedArea))) {
          this.selectedArea = String(decryptedArea);
          localStorage.removeItem('ddval');
          await this.loadReport(true);
        }
      }
    } catch {
      if (requestVersion !== this.requestVersion) return;
      this.hasError = true;
      this.notificationService.showError('خطای داخلی رخ داده است !');
    } finally {
      if (requestVersion === this.requestVersion) this.setLoading(false);
    }
  }

  async loadReport(allowWhileLoading = false): Promise<void> {
    if (this.loading && !allowWhileLoading) return;
    if (!this.definition.endpoint) return;
    if (this.isAreaSelectionReport && !this.selectedArea) {
      this.hasRequestedData = false;
      this.rows = [];
      this.refreshTable(true);
      return;
    }
    if (this.config.mode === 'inspection-detail' && (!this.filterStartDate || !this.filterEndDate)) {
      this.notificationService.showError('تاریخ شروع و تاریخ پایان را وارد کنید');
      return;
    }
    if (this.config.mode === 'inspection-detail') this.filtersOpen = false;

    const requestVersion = ++this.requestVersion;
    this.setLoading(true);
    this.hasError = false;
    this.hasRequestedData = true;
    try {
      const result = await this.api.loadRows(this.definition.endpoint, this.buildPayload());
      if (requestVersion !== this.requestVersion) return;
      if (this.handleStatus(result)) return;
      const rows = Array.isArray(result) ? result : [];
      this.rows = rows.map((row) => ({ ...row, searchIndex: buildGeofenceSearchIndex(row) }));
      this.refreshTable(true);
    } catch {
      if (requestVersion !== this.requestVersion) return;
      this.rows = [];
      this.hasError = true;
      this.refreshTable(true);
      this.notificationService.showError('خطای داخلی رخ داده است !');
    } finally {
      if (requestVersion === this.requestVersion) this.setLoading(false);
    }
  }

  onAreaChange(): void {
    if (this.loading) return;
    this.searchText = '';
    this.filtersOpen = false;
    this.loadReport();
  }

  toggleFilters(): void {
    if (this.loading) return;
    this.filtersOpen = !this.filtersOpen;
  }

  onSearchChange(): void {
    this.refreshTable(true);
  }

  clearSearch(): void {
    this.searchText = '';
    this.refreshTable(true);
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.refreshTable(true);
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) return;
    this.currentPage = page;
    this.refreshTable();
  }

  rowClicked(row: GeofenceReportRow): void {
    if (this.config.rowAction === 'location') this.openLocation(row);
    if (this.config.rowAction === 'entry-exit-map' && row.iArrivalsAndDepartures != null) {
      const suffix = this.context.polygonId ? `/${this.context.polygonId}` : '';
      this.router.navigate([`/stopMap/${row.iArrivalsAndDepartures}!${suffix}`]);
    }
  }

  openInspectionRoute(row: GeofenceReportRow, movement: 'inside' | 'next', event: Event): void {
    event.stopPropagation();
    this.storeEncrypted('RouteDate', row.strDate);
    this.storeEncrypted('reportStime', movement === 'inside' ? row.strEnterTime : row.strExitTime);
    this.storeEncrypted('reportEtime', movement === 'inside' ? row.strExitTime : row.strEnterNextTime);
    window.open(this.router.serializeUrl(this.router.createUrlTree([`route/${this.context.id}`])), '_blank');
  }

  async openFullInspectionRoute(): Promise<void> {
    if (this.loading || !this.rows.length) return;
    const first = this.rows[0];
    const last = this.rows[this.rows.length - 1];
    this.storeEncrypted('RouteDate', first.strDate);
    this.storeEncrypted('reportStime', first.strExitTime);
    this.storeEncrypted('reportEtime', last.strEnterTime);
    this.setLoading(true);
    try {
      const result = await this.api.loadInspectionZones({ ...this.credentials, strUnitID: first.strUnitID || this.context.id });
      if (this.handleStatus(result)) return;
      localStorage.setItem('inspectionZones', this.gurdService.encrypt(String(result)));
      window.open(this.router.serializeUrl(this.router.createUrlTree([`route/${this.context.id}`])), '_blank');
    } catch {
      this.notificationService.showError('دریافت مسیر حرکت با خطا مواجه شد');
    } finally {
      this.setLoading(false);
    }
  }

  async deleteInspectionAnalysis(): Promise<void> {
    if (this.loading) return;
    if (!window.confirm('آیا از حذف تحلیل‌های قبلی اطمینان دارید؟')) return;
    this.setLoading(true);
    try {
      const result = await this.api.deleteInspection({ ...this.credentials, strUnitID: this.context.id, strSDate: this.filterStartDate, strEDate: this.filterEndDate });
      if (this.handleStatus(result)) return;
      this.rows = [];
      this.hasRequestedData = true;
      this.refreshTable(true);
      this.notificationService.showSuccess('تحلیل با موفقیت حذف شد');
    } catch {
      this.notificationService.showError('حذف تحلیل با خطا مواجه شد');
    } finally {
      this.setLoading(false);
    }
  }

  exportExcel(): void {
    this.exportService.exportRows(
      this.definition.fileName,
      ['#', ...this.config.columns.map((column) => column.title)],
      this.filteredRows.map((row, index) => [index + 1, ...this.config.columns.map((column) => this.exportCellValue(row, column))]),
    );
  }

  cellValue(row: GeofenceReportRow, column: GeofenceColumn): string | number {
    if (column.format === 'plate') return row.strVehicleNo ? this.otherService.returnCorrectPlate(row.strVehicleNo) : '';
    if (column.format === 'number') return Number(row[column.field]) || 0;
    return row[column.field] == null ? '' : String(row[column.field]);
  }

  statusClass(row: GeofenceReportRow): string {
    if (Number(row.tiStatus) === 2) return 'ok';
    if (Number(row.tiStatus) === 1) return 'bad';
    return 'warn';
  }

  totalValue(column: GeofenceColumn): string | number {
    const value = this.totals[String(column.field)] || 0;
    if (column.total === 'minutes') return formatMinutes(value);
    if (column.total === 'seconds') return formatSeconds(value);
    return value;
  }

  goBack(): void {
    this.location.back();
  }

  get isAreaSelectionReport(): boolean {
    return this.config?.mode === 'presence' || this.config?.mode === 'last-presence';
  }
  get hasFilterSection(): boolean { return this.isAreaSelectionReport || this.config?.mode === 'inspection-detail'; }
  get requiresFilterBeforeLoad(): boolean { return this.isAreaSelectionReport || this.config?.mode === 'inspection-detail'; }
  get filterCount(): number {
    if (this.isAreaSelectionReport) return this.selectedArea ? 1 : 0;
    return [this.filterStartDate, this.filterEndDate].filter(Boolean).length;
  }
  get filterStartDate(): string { return String(this.inspectionFilterForm.get('startDate')?.value || ''); }
  get filterEndDate(): string { return String(this.inspectionFilterForm.get('endDate')?.value || ''); }
  get hasActiveFilters(): boolean { return !!this.searchText.trim(); }
  get hasTotals(): boolean { return this.config.columns.some((column) => !!column.total); }
  get actionColumns(): GeofenceColumn[] { return this.config.columns.filter((column) => column.format === 'actions'); }
  get dataColumns(): GeofenceColumn[] { return this.config.columns.filter((column) => column.format !== 'actions'); }
  get tableId(): string { return `geofence-report-${this.config.mode}`; }
  get printSectionId(): string { return `geofence-report-print-${this.config.mode}`; }
  get searchPlaceholder(): string { return `جستجو در جدول: ${(this.definition.searchFields || []).join('، ')}...`; }
  get subtitle(): string {
    if (this.isAreaSelectionReport) {
      const area = this.areas.find((item) => String(item.iGeoFence) === this.selectedArea);
      return area ? `محدوده: ${area.strTitle}` : 'برای نمایش اطلاعات، محدوده را انتخاب کنید';
    }
    const startDate = this.config.mode === 'inspection-detail' ? this.filterStartDate : this.context.startDate;
    const endDate = this.config.mode === 'inspection-detail' ? this.filterEndDate : this.context.endDate;
    return `از تاریخ ${startDate || '-'} الی ${endDate || '-'}`;
  }
  get emptyTitle(): string {
    if (this.hasActiveFilters) return 'با این فیلترها نتیجه‌ای پیدا نشد!';
    if (this.isAreaSelectionReport && !this.selectedArea) return 'ابتدا یک محدوده انتخاب کنید';
    if (this.config.mode === 'inspection-detail' && !this.hasRequestedData) return 'بازهٔ تاریخ را مشخص و گزارش را بررسی کنید';
    return 'اطلاعاتی برای نمایش وجود ندارد';
  }
  get emptyDescription(): string {
    if (this.hasActiveFilters) return 'عبارت جستجو را تغییر دهید و دوباره تلاش کنید.';
    if ((this.isAreaSelectionReport && !this.selectedArea) || (this.config.mode === 'inspection-detail' && !this.hasRequestedData)) return 'پس از تعیین فیلتر، اطلاعات این گزارش نمایش داده می‌شود.';
    return 'برای این گزارش داده‌ای ثبت نشده است.';
  }

  private get credentials(): ReportCredentials {
    return {
      iSystemUser: this.gurdService.decrypt(localStorage.getItem('ID')!),
      strSession: this.gurdService.decrypt(localStorage.getItem('Session')!),
    };
  }

  private buildPayload(): Record<string, unknown> {
    if (this.isAreaSelectionReport) return { ...this.credentials, iGeoFence: this.selectedArea };
    if (this.config.mode === 'inspection-summary') return { ...this.credentials, strSDate: this.context.startDate, strEDate: this.context.endDate };
    if (this.config.mode === 'inspection-detail') return { ...this.credentials, strUnitID: this.context.id, strSDate: this.filterStartDate, strEDate: this.filterEndDate };
    return {
      ...this.credentials,
      strUnitID: this.context.id,
      strSDate: this.context.startDate,
      strEDate: this.context.endDate,
      ...(this.config.mode === 'area-analysis' || this.config.mode === 'polygon-entry-exit'
        ? { strSTime: this.context.startTime, strETime: this.context.endTime, iGeoFence: this.context.polygonId }
        : {}),
    };
  }

  private refreshTable(resetPage = false): void {
    if (!this.config) return;
    if (resetPage) this.currentPage = 1;
    const query = toEnglishDigits(this.searchText).trim().toLowerCase();
    this.filteredRows = query ? this.rows.filter((row) => row.searchIndex?.includes(query)) : this.rows;
    this.totals = {};
    for (const column of this.config.columns) {
      if (column.total) this.totals[String(column.field)] = sumGeofenceField(this.filteredRows, column.totalField || column.field);
    }
    this.totalPages = Math.max(1, Math.ceil(this.filteredRows.length / this.pageSize));
    this.currentPage = Math.min(this.currentPage, this.totalPages);
    const start = (this.currentPage - 1) * this.pageSize;
    this.pagedRows = this.filteredRows.slice(start, start + this.pageSize);
  }

  private handleStatus(result: unknown): boolean {
    if (!result || Array.isArray(result) || typeof result !== 'object') return false;
    const status = result as GeofenceApiStatus;
    if (status.ISessionExpired || status.ISSessionExpired) {
      this.notificationService.showError('لطفا وارد سیستم شوید');
      this.otherService.removeLocalStorage();
      this.router.navigate(['login'], { queryParams: { report: this.definition.loginReport || this.config.mode } });
      return true;
    }
    if (status.iResult === 0) {
      this.notificationService.showError(status.strError || 'دریافت اطلاعات با خطا مواجه شد');
      this.hasError = true;
      return true;
    }
    return false;
  }

  private openLocation(row: GeofenceReportRow): void {
    const latitude = this.config.mode === 'last-presence' ? row.dLatitude : row.fLat;
    const longitude = this.config.mode === 'last-presence' ? row.dLongitude : row.fLon;
    const model = { strDate: row.strDate || '', strAddress: row.strAddress || '', strSTime: row.strSTime || '', strETime: row.strETime || '', strParkTime: row.strParkTime || '' };
    if (this.config.mode === 'presence') this.storeEncrypted('nameOfDriv', row.strFullName);
    localStorage.setItem('ddval', this.gurdService.encrypt(this.selectedArea));
    this.storeEncrypted('StopModel', JSON.stringify(model));
    this.storeEncrypted('fLat', latitude);
    this.storeEncrypted('fLon', longitude);
    this.router.navigate(['stopMap']);
  }

  private storeEncrypted(key: string, value: unknown): void {
    localStorage.setItem(key, this.gurdService.encrypt(String(value ?? '')));
  }

  private setLoading(loading: boolean): void {
    this.loading = loading;
    if (this.config?.mode !== 'inspection-detail') return;
    if (loading) this.inspectionFilterForm.disable({ emitEvent: false });
    else this.inspectionFilterForm.enable({ emitEvent: false });
  }

  private exportCellValue(row: GeofenceReportRow, column: GeofenceColumn): string | number {
    if (column.format === 'actions') return '';
    return this.cellValue(row, column);
  }
}
